import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import AlarmOffIcon from "@mui/icons-material/AlarmOff";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { SnoozedTicket, haloTicketUrl, theoTicketUrl } from "../../apis/ticketLifecycleApi";
import { CollapseToggle, InsightSections } from "./TechnicianOverview";
import theoLogo from "../../assets/img/theo_logo.svg";

interface SnoozedRowProps {
  ticket: SnoozedTicket;
  onUnsnooze: (ticketId: string | number) => Promise<void>;
  haloBaseUrl?: string;
}

const SnoozedRow: React.FC<SnoozedRowProps> = ({ ticket, onUnsnooze, haloBaseUrl }) => {
  const [unsnoozing, setUnsnoozing] = React.useState(false);

  const handleUnsnooze = async () => {
    setUnsnoozing(true);
    try {
      await onUnsnooze(ticket.ticket_id);
    } finally {
      setUnsnoozing(false);
    }
  };

  const snoozedUntil = new Date(ticket.snoozed_until).toLocaleDateString();
  const haloUrl = haloTicketUrl(haloBaseUrl ?? "", ticket.ticket_id);

  return (
    <ListItem
      divider
      secondaryAction={
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Tooltip title="Open in Theo" arrow>
            <IconButton
              component={RouterLink}
              to={theoTicketUrl(ticket.ticket_id)}
              aria-label={`Open ${ticket.ticket_title} in Theo`}
              size="small"
            >
              <Box component="img" src={theoLogo} alt="Theo" sx={{ width: 18, height: 18 }} />
            </IconButton>
          </Tooltip>
          <IconButton
            aria-label={`Unsnooze ${ticket.ticket_title}`}
            onClick={handleUnsnooze}
            disabled={unsnoozing}
            size="small"
          >
            {unsnoozing ? <CircularProgress size={16} /> : <AlarmOffIcon fontSize="small" />}
          </IconButton>
        </Box>
      }
    >
      <ListItemText
        primary={
          <Typography variant="body2" fontWeight={600} color="text.secondary">
            {haloUrl ? (
              <Link
                href={haloUrl}
                target="_blank"
                rel="noopener"
                underline="always"
                sx={{ color: "primary.main" }}
              >
                #{ticket.ticket_id}
              </Link>
            ) : (
              <>#{ticket.ticket_id}</>
            )}
            <Box component="span" sx={{ ml: 1.5 }}>
              {ticket.ticket_title}
            </Box>
          </Typography>
        }
        secondary={
          <Box component="span">
            <Typography component="span" variant="caption" color="text.secondary">
              Snoozed until: {snoozedUntil}
            </Typography>
            {ticket.client_name && (
              <>
                {" · "}
                <Typography component="span" variant="caption" color="text.secondary">
                  {ticket.client_name}
                </Typography>
              </>
            )}
            {ticket.upcoming_calls.length > 0 && (
              <>
                {" · "}
                <Typography component="span" variant="caption" color="primary">
                  {ticket.upcoming_calls.length} upcoming call
                  {ticket.upcoming_calls.length !== 1 ? "s" : ""}
                </Typography>
              </>
            )}
            {ticket.summary && (
              <Box component="span" sx={{ display: "block" }}>
                <InsightSections raw={ticket.summary} />
              </Box>
            )}
          </Box>
        }
      />
    </ListItem>
  );
};

interface SnoozedGroupProps {
  agentName: string;
  tickets: SnoozedTicket[];
  onUnsnooze: (ticketId: string | number) => Promise<void>;
  allExpanded?: boolean;
  expandEpoch?: number;
  haloBaseUrl?: string;
}

const SnoozedGroup: React.FC<SnoozedGroupProps> = ({ agentName, tickets, onUnsnooze, allExpanded = false, expandEpoch = 0, haloBaseUrl }) => {
  const [open, setOpen] = React.useState(false);

  // Respond to the panel's "expand/collapse all" toggle (see TechnicianOverview).
  React.useEffect(() => {
    if (expandEpoch > 0) setOpen(allExpanded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandEpoch]);

  const upcomingCallsCount = tickets.reduce((sum, t) => sum + t.upcoming_calls.length, 0);

  return (
    <Card sx={{ mb: 2, boxShadow: 1, bgcolor: "grey.50", opacity: 0.85 }}>
      <CardContent>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 2, cursor: "pointer" }}
          onClick={() => setOpen((v) => !v)}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            {agentName}
          </Typography>
          <Chip label={`${tickets.length} snoozed`} size="small" />
          {upcomingCallsCount > 0 && (
            <Typography variant="caption" color="primary">
              {upcomingCallsCount} upcoming call{upcomingCallsCount !== 1 ? "s" : ""}
            </Typography>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <IconButton size="small" aria-label={open ? "Collapse" : "Expand"}>
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List dense disablePadding sx={{ mt: 1 }}>
            {tickets.map((ticket) => (
              <SnoozedRow
                key={String(ticket.ticket_id)}
                ticket={ticket}
                onUnsnooze={onUnsnooze}
                haloBaseUrl={haloBaseUrl}
              />
            ))}
          </List>
        </Collapse>
      </CardContent>
    </Card>
  );
};

interface SnoozedTicketsProps {
  data: SnoozedTicket[];
  onUnsnooze: (ticketId: string | number) => Promise<void>;
  haloBaseUrl?: string;
}

const SnoozedTickets: React.FC<SnoozedTicketsProps> = ({ data, onUnsnooze, haloBaseUrl }) => {
  // Snoozed groups default collapsed, so this toggle starts as "Expand all".
  const [snoozedExpanded, setSnoozedExpanded] = React.useState(false);
  const [snoozedEpoch, setSnoozedEpoch] = React.useState(0);
  const toggleSnoozed = () => {
    setSnoozedExpanded((v) => !v);
    setSnoozedEpoch((e) => e + 1);
  };
  // Group snoozed tickets by technician (like the Technician Overview),
  // unassigned last, otherwise alphabetical.
  const groups = new Map<string, SnoozedTicket[]>();
  for (const ticket of data) {
    const name = ticket.agent_name ?? "Unassigned";
    if (!groups.has(name)) groups.set(name, []);
    groups.get(name)!.push(ticket);
  }
  const groupNames = Array.from(groups.keys()).sort((a, b) => {
    if (a === "Unassigned") return 1;
    if (b === "Unassigned") return -1;
    return a.localeCompare(b);
  });

  return (
    <Box sx={{ mt: 2, mb: 6 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 1 }}>
        <Typography variant="h6" fontWeight={700} color="text.secondary">
          Snoozed Tickets
        </Typography>
        {data.length > 0 && <CollapseToggle expanded={snoozedExpanded} onToggle={toggleSnoozed} />}
      </Box>
      {data.length === 0 ? (
        <Card sx={{ boxShadow: 1, bgcolor: "grey.50", opacity: 0.85 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              No snoozed tickets.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        groupNames.map((name) => (
          <SnoozedGroup
            key={name}
            agentName={name}
            tickets={groups.get(name)!}
            onUnsnooze={onUnsnooze}
            allExpanded={snoozedExpanded}
            expandEpoch={snoozedEpoch}
            haloBaseUrl={haloBaseUrl}
          />
        ))
      )}
    </Box>
  );
};

export default SnoozedTickets;
